import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `interactive` adds press feedback for tappable cards (kiosk menu items). */
  variant?: 'default' | 'interactive' | 'raised'
  padded?: boolean
}

export function Card({
  variant = 'default',
  padded = true,
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-surface-border bg-surface-raised',
        padded && 'p-4',
        variant === 'raised' && 'shadow-card bg-surface-overlay',
        variant === 'interactive' &&
          'cursor-pointer transition-all duration-150 hover:border-surface-border-strong active:scale-[0.99] touch-surface',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function CardHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mb-3 flex items-start justify-between gap-3', className)} {...props}>
      {children}
    </div>
  )
}

export function CardTitle({
  as: Component = 'h3',
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { as?: 'h2' | 'h3' | 'h4' }) {
  return (
    <Component
      className={cn('font-semibold text-content text-lg leading-tight', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function CardBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('text-content-secondary', className)} {...props}>
      {children}
    </div>
  )
}

export function CardFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode }) {
  return (
    <div
      className={cn('mt-4 flex items-center gap-3 border-surface-border border-t pt-3', className)}
      {...props}
    >
      {children}
    </div>
  )
}
